/**
 * The shape of a series, beside the figures it belongs to.
 *
 * Deliberately unreadable as values: no axes, no grid, no labels. It answers
 * "did this climb, fall or wander" in the width of a metric, and anyone who
 * needs a number reads the number next to it. A sparkline with a tooltip is a
 * chart that has outgrown its slot.
 *
 * The path is geometry, not money: the points arrive measured and are mapped
 * to a viewBox. Nothing here rounds a value anybody will read.
 */
import { useId } from "react";

import { finiteGeometryValue } from "@/shared/money/geometry";

type Point = { readonly at: string; readonly value: string };

const WIDTH = 160;
const HEIGHT = 36;

function path(points: readonly Point[]): string | null {
  if (points.length < 2) return null;
  const raw = points.map((point) => finiteGeometryValue(point.value));
  if (raw.some((value) => value === null)) return null;
  const values = raw as readonly number[];

  const low = Math.min(...values);
  const high = Math.max(...values);
  // A flat series has no range to divide by; draw it down the middle rather
  // than dividing by zero or pretending it moved.
  const span = high - low || 1;
  const step = WIDTH / (values.length - 1);

  return values
    .map((value, index) => {
      const x = index * step;
      const y = HEIGHT - ((value - low) / span) * HEIGHT;
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

export function Sparkline({
  points,
  label,
  rising,
  testId,
}: {
  readonly points: readonly Point[];
  readonly label: string;
  /** Which way it ended, so the line is not the only thing saying so. */
  readonly rising: boolean;
  readonly testId?: string;
}): React.JSX.Element | null {
  const gradient = useId();
  const drawn = path(points);
  if (drawn === null) return null;

  return (
    <svg
      role="img"
      aria-label={label}
      data-testid={testId}
      data-direction={rising ? "up" : "down"}
      viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
      preserveAspectRatio="none"
      className="h-full min-h-9 w-full"
    >
      {/* Filled under the line, like the chart it stands in for. At this
          height a hairline is a scratch on the panel; the fill is what makes
          the shape readable at a glance, which is the whole job.

          The gradient is defined inside the coloured group on purpose:
          `currentColor` resolves against the element the stop sits in, so a
          definition in a bare <defs> takes the svg's colour and the fill comes
          out grey. */}
      <g className={rising ? "text-chart-positive" : "text-chart-negative"}>
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity={0.3} />
            <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path
          d={`${drawn} L${String(WIDTH)},${String(HEIGHT)} L0,${String(HEIGHT)} Z`}
          fill={`url(#${gradient})`}
          stroke="none"
        />
        <path
          d={drawn}
          fill="none"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
          className="stroke-current"
        />
      </g>
    </svg>
  );
}
